import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Row, Spin, Table } from 'antd'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import { CaretDownFilled, CaretRightFilled, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons'
import { TYPE_GROUP, TYPE_TOTAL } from 'src/models/pnlConfig'
import { wtSubYearsToDate } from 'src/helpers/commons/moment'
import Pdf from './Pdf'

const StockAdjustmentReport = () => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [reportData, setReportData] = useState([])
  const [date, setDate] = useState('')
  const lastYearLastDate = date ? wtSubYearsToDate(date, 1, 'YYYY-MM-DD') : ''
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [expandedRows, setExpandedRows] = useState([])
  const [boldRows, setBoldRows] = useState([])

  const branches = wtGetFromLocalStorage('branches')
  const companyName = wtGetFromLocalStorage('company_name')

  const formatReportValue = (value) => {
    const numericValue = Number(value || 0)
    const formattedValue = wtFormatMoney(Math.abs(numericValue), '', true, 0).replace(/\s+/g, '')

    return numericValue < 0 ? `(${formattedValue})` : formattedValue
  }

  useEffect(() => {
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)

    const dateParam = searchParams.get('date')
    const branchIdParam = searchParams.get('branchId')
    const branchNameParam = searchParams.get('branchName')

    if (dateParam) {
      setDate(dateParam)
    }

    if (+branchIdParam > 0) {
      setBranchId(+branchIdParam)
      setBranchName(branchNameParam || '')
    }
  }, [])

  const resetReportState = () => {
    setReportData([])
    setExpandedRows([])
    setBoldRows([])
  }

  const changeValues = (key, val) => {
    resetReportState()

    if (key === 'date') {
      setDate(val)
    }

    if (key === 'branch_id') {
      setBranchId(val?.value || 0)
      setBranchName(val?.label || '')
    }
  }

  const validateFilters = () => {
    if (!date) {
      lzNotifyErrorMessageFromResult('Please select Date!')
      return false
    }

    if (!branchId) {
      lzNotifyErrorMessageFromResult('Please select Branch!')
      return false
    }

    return true
  }

  const getReportData = () => {
    if (!validateFilters()) {
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/stock_adjustment_report',
      {
        date,
        branch_id: branchId,
      },
      'get',
      (response) => {
        setReportData(response?.data?.stock_adjustment_report_data || [])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportToPdf = async () => {
    setIsLoading(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 500))
      Pdf(reportData, date, lastYearLastDate, branchName)
    } finally {
      setIsLoading(false)
    }
  }

  const getFirstLevelData = (data = []) => {
    let noteValue = 0

    return data.map((row) => ({
      name: row.name,
      note: ++noteValue,
      current_year_balance: row.current_year_balance,
      last_year_balance: row.last_year_balance,
      is_heading: row.is_heading,
      type: row.type,
      hasItems: row.hasItems,
      children: Array.isArray(row.children) ? row.children : [],
    }))
  }

  const createNestedNotesData = (rows = [], depth = 0, parentNote = '') => {
    let notesRows = []
    let childNoteValue = 0

    rows.forEach((row) => {
      const fullNote = `${parentNote}.${++childNoteValue}`
      const indent = '    '.repeat(depth + 1)

      notesRows.push({
        description: indent + row.name,
        note: fullNote,
        current_year_balance: Number(row.current_year_balance || 0),
        last_year_balance: Number(row.last_year_balance || 0),
      })

      if (Array.isArray(row.children) && row.children.length > 0) {
        notesRows = [...notesRows, ...createNestedNotesData(row.children, depth + 1, fullNote)]
      }
    })

    return notesRows
  }

  const createNotesData = (row, depth = 0, parentNote = '') => {
    let notesRows = []
    let childNoteValue = 0
    const children = Array.isArray(row.children) ? row.children : []

    notesRows.push({
      description: `${row.note}  ${row.name}`.trim(),
      note: '',
      current_year_balance: '',
      last_year_balance: '',
    })

    children.forEach((child) => {
      const childNote = parentNote || row.note
      const fullNote = `${childNote}.${++childNoteValue}`
      const indent = '    '.repeat(depth + 1)

      notesRows.push({
        description: indent + child.name,
        note: fullNote,
        current_year_balance: Number(child.current_year_balance || 0),
        last_year_balance: Number(child.last_year_balance || 0),
      })

      if (Array.isArray(child.children) && child.children.length > 0) {
        notesRows = [...notesRows, ...createNestedNotesData(child.children, depth + 1, fullNote)]
      }
    })

    return notesRows
  }

  const exportToExcel = () => {
    const summaryData = getFirstLevelData(reportData)
    const notesData = getFirstLevelData(reportData).flatMap((row) => createNotesData(row))
    const escapeXml = (value) =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')

    const cell = (value, styleId = '', type = 'String') => {
      const styleAttr = styleId ? ` ss:StyleID="${styleId}"` : ''

      if (value === '' || value === null || typeof value === 'undefined') {
        return `<Cell${styleAttr}/>`
      }

      if (type === 'Number') {
        return `<Cell${styleAttr}><Data ss:Type="Number">${value}</Data></Cell>`
      }

      return `<Cell${styleAttr}><Data ss:Type="${type}">${escapeXml(value)}</Data></Cell>`
    }

    const rowXml = (cells) => `<Row>${cells.join('')}</Row>`

    const summaryRowsXml = [
      rowXml([cell('Stock Adjustment Report', 'title')]),
      rowXml([cell('Branch', 'metaLabel'), cell(branchName || 'All', 'metaValue')]),
      rowXml([cell('As Of', 'metaLabel'), cell(date || '', 'metaValue')]),
      rowXml([cell('Comparative As Of', 'metaLabel'), cell(lastYearLastDate || '','metaValue')]),
      rowXml([]),
      rowXml([
        cell('Description', 'header'),
        cell('Note', 'header'),
        cell(`As Of ${date}`, 'currentHeader'),
        cell(`As Of ${lastYearLastDate}`, 'lastHeader'),
      ]),
      ...summaryData.map((row) =>
        rowXml([
          cell(
            row.name,
            row.type === TYPE_TOTAL
              ? 'totalText'
              : row.is_heading === 1 || row.hasItems
                ? 'boldText'
                : 'text',
          ),
          cell(
            row.note,
            row.type === TYPE_TOTAL
              ? 'totalCenter'
              : row.is_heading === 1 || row.hasItems
                ? 'boldCenter'
                : 'center',
          ),
          row.is_heading === 1
            ? cell(
                '',
                row.type === TYPE_TOTAL
                  ? 'currentValueTotal'
                  : row.hasItems
                    ? 'currentValueBold'
                    : 'currentValue',
              )
            : cell(
                Number(row.current_year_balance || 0),
                row.type === TYPE_TOTAL
                  ? 'currentValueTotal'
                  : row.hasItems
                    ? 'currentValueBold'
                    : 'currentValue',
                'Number',
              ),
          row.is_heading === 1
            ? cell(
                '',
                row.type === TYPE_TOTAL
                  ? 'lastValueTotal'
                  : row.hasItems
                    ? 'lastValueBold'
                    : 'lastValue',
              )
            : cell(
                Number(row.last_year_balance || 0),
                row.type === TYPE_TOTAL
                  ? 'lastValueTotal'
                  : row.hasItems
                    ? 'lastValueBold'
                    : 'lastValue',
                'Number',
              ),
        ]),
      ),
    ]

    const notesRowsXml = [
      rowXml([cell('Stock Adjustment Report Notes', 'notesTitle')]),
      rowXml([cell('Branch', 'metaLabel'), cell(branchName || 'All', 'metaValue')]),
      rowXml([cell('As Of', 'metaLabel'), cell(date || '', 'metaValue')]),
      rowXml([]),
      rowXml([
        cell('Description', 'notesHeader'),
        cell('Note', 'notesHeader'),
        cell(`As Of ${date}`, 'currentHeader'),
        cell(`As Of ${lastYearLastDate}`, 'lastHeader'),
      ]),
      ...notesData.map((row) => {
        const isSection = row.note === '' && row.current_year_balance === ''
        const noteDepth = row.note ? row.note.toString().split('.').length : 0
        const isIndented = noteDepth > 2
        const noteStyle = isSection
          ? 'notesSectionCenter'
          : noteDepth > 2
            ? 'notesCenterSecondary'
            : 'notesCenterPrimary'

        return rowXml([
          cell(
            row.description,
            isSection
              ? 'notesSection'
              : row.current_year_balance !== '' && row.note === ''
                ? 'notesTotalText'
                : isIndented
                  ? 'notesChildText'
                  : 'notesText',
          ),
          cell(row.note, noteStyle),
          row.current_year_balance === ''
            ? cell('', isSection ? 'currentValueBold' : 'currentValue')
            : cell(
                row.current_year_balance,
                isSection
                  ? 'currentValueBold'
                  : row.note === ''
                    ? 'currentValueTotal'
                    : isIndented
                      ? 'currentValueSoft'
                      : 'currentValue',
                'Number',
              ),
          row.last_year_balance === ''
            ? cell('', isSection ? 'lastValueBold' : 'lastValue')
            : cell(
                row.last_year_balance,
                isSection
                  ? 'lastValueBold'
                  : row.note === ''
                    ? 'lastValueTotal'
                    : isIndented
                      ? 'lastValueSoft'
                      : 'lastValue',
                'Number',
              ),
        ])
      }),
    ]

    const workbookXml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
 <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="title">
   <Font ss:FontName="Arial" ss:Size="15" ss:Bold="1" ss:Color="#1F1F1F"/>
   <Interior ss:Color="#D9EAF7" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="notesTitle">
   <Font ss:FontName="Arial" ss:Size="15" ss:Bold="1" ss:Color="#7C2D12"/>
  </Style>
  <Style ss:ID="metaLabel">
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#1F1F1F"/>
   <Interior ss:Color="#F3F6FA" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="metaValue">
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#0B3D91"/>
   <Interior ss:Color="#F9FBFD" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="header">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#3C4B64" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="notesHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#7C2D12"/>
  </Style>
  <Style ss:ID="currentHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#D9F3FF"/>
   <Interior ss:Color="#0E7490" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="lastHeader">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#FFE7D6"/>
   <Interior ss:Color="#A64C1D" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="text">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="boldText">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="totalText">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="notesText">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#7C2D12"/>
  </Style>
  <Style ss:ID="notesChildText">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#C2410C"/>
  </Style>
  <Style ss:ID="notesSection">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#9A3412"/>
  </Style>
  <Style ss:ID="notesTotalText">
   <Alignment ss:Horizontal="Left" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#9A3412"/>
  </Style>
  <Style ss:ID="center">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="boldCenter">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="totalCenter">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="notesCenterPrimary">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="notesCenterSecondary">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Color="#0B3D91"/>
  </Style>
  <Style ss:ID="notesSectionCenter">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#1F1F1F"/>
  </Style>
  <Style ss:ID="currentValue">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#0284C7"/>
  </Style>
  <Style ss:ID="currentValueSoft">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#0EA5E9"/>
  </Style>
  <Style ss:ID="currentValueBold">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#0369A1"/>
  </Style>
  <Style ss:ID="currentValueTotal">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#0369A1"/>
  </Style>
  <Style ss:ID="lastValue">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#EA580C"/>
  </Style>
  <Style ss:ID="lastValueSoft">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#F97316"/>
  </Style>
  <Style ss:ID="lastValueBold">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#C2410C"/>
  </Style>
  <Style ss:ID="lastValueTotal">
   <Alignment ss:Horizontal="Right" ss:Vertical="Center"/>
   <Borders>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
   </Borders>
   <NumberFormat ss:Format="#,##0;(#,##0)"/>
   <Font ss:FontName="Arial" ss:Size="12" ss:Bold="1" ss:Color="#C2410C"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Summary">
  <Table>
   <Column ss:Width="260"/>
   <Column ss:Width="70"/>
   <Column ss:Width="110"/>
   <Column ss:Width="110"/>
   ${summaryRowsXml.join('')}
  </Table>
 </Worksheet>
 <Worksheet ss:Name="Notes">
  <Table>
   <Column ss:Width="320"/>
   <Column ss:Width="90"/>
   <Column ss:Width="110"/>
   <Column ss:Width="110"/>
   ${notesRowsXml.join('')}
  </Table>
 </Worksheet>
</Workbook>`

    const blob = new Blob([workbookXml], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.download = `stock_adjustment_report_${branchName || 'all'}_${date || 'report'}.xls`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const getDescendantKeys = (rows = []) => {
    let keys = []

    rows.forEach((row) => {
      if (row?.key !== undefined && row?.key !== null) {
        keys.push(row.key)
      }

      if (Array.isArray(row?.children) && row.children.length > 0) {
        keys = [...keys, ...getDescendantKeys(row.children)]
      }
    })

    return keys
  }

  const getExpandableKeys = (rows = []) => {
    let keys = []

    rows.forEach((row) => {
      if (row?.hasItems && row?.key !== undefined && row?.key !== null) {
        keys.push(row.key)
      }

      if (Array.isArray(row?.children) && row.children.length > 0) {
        keys = [...keys, ...getExpandableKeys(row.children)]
      }
    })

    return keys
  }

  const handleExpand = (record, forceExpanded = null) => {
    const key = record.key
    if (record.hasItems) {
      const isExpanded =
        typeof forceExpanded === 'boolean' ? forceExpanded : expandedRows.includes(key)

      if (isExpanded) {
        setExpandedRows((prevExpandedRows) => {
          if (prevExpandedRows.includes(key)) {
            return prevExpandedRows.filter((rowKey) => rowKey !== key)
          }

          return [...prevExpandedRows, key]
        })
        setBoldRows((prevBoldRows) => {
          if (prevBoldRows.includes(record.key)) {
            return prevBoldRows
          }

          return [...prevBoldRows, record.key]
        })
      } else {
        const descendantKeys = getDescendantKeys(record.children || [])
        const keysToRemove = [key, ...descendantKeys]

        setExpandedRows((prevExpandedRows) =>
          prevExpandedRows.filter((rowKey) => !keysToRemove.includes(rowKey)),
        )
        setBoldRows((prevBoldRows) =>
          prevBoldRows.filter((rowKey) => !keysToRemove.includes(rowKey)),
        )
      }
    }
  }

  const expandAllRows = () => {
    const allExpandableKeys = getExpandableKeys(reportData)
    setExpandedRows(allExpandableKeys)
    setBoldRows(allExpandableKeys)
  }

  const collapseAllRows = () => {
    setExpandedRows([])
    setBoldRows([])
  }

  const expandIcon = ({ expanded, onExpand, record }) => {
    if (record.hasItems) {
      return (
        <span style={{ marginRight: '2%' }}>
          {expanded ? (
            <CaretDownFilled
              style={{ color: '#706f71', fontSize: 12 }}
              onClick={(e) => {
                e.stopPropagation()
                handleExpand(record, false)
              }}
            />
          ) : (
            <CaretRightFilled
              style={{ color: '#706f71', fontSize: 12 }}
              onClick={(e) => {
                e.stopPropagation()
                handleExpand(record, true)
              }}
            />
          )}
        </span>
      )
    }

    return <span style={{ marginRight: '3%' }}></span>
  }

  const columns = [
    {
      title: <span style={{ fontSize: '0.8rem', marginLeft: '5%' }}>Description</span>,
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: '70%',
      onCell: (record) => ({
        onClick: () => handleExpand(record),
        style: { cursor: record.hasItems ? 'pointer' : 'default' },
      }),
      render: (text, record) => (
        <span
          style={{
            fontWeight:
              record.type === TYPE_TOTAL || boldRows.includes(record.key) || record.is_heading === 1
                ? 'bold'
                : 'normal',
            fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: '0.8rem' }}>{date ? `As Of ${date}` : 'Balance'}</span>,
      align: 'right',
      children: [
        {
          title: <span style={{ fontSize: '0.8rem' }}>Balance ( .Rs. )</span>,
          dataIndex: 'current_year_balance',
          key: 'current_year_balance',
          align: 'right',
          render: (text, record) => (
            <span
              style={{
                fontWeight:
                  record.type === TYPE_TOTAL || boldRows.includes(record.key) || record.is_heading === 1
                    ? 'bold'
                    : 'normal',
                fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
              }}
            >
              {record.is_heading === 1 ? '' : formatReportValue(text)}
            </span>
          ),
        },
      ],
    },
    {
      title: <span style={{ fontSize: '0.8rem' }}>{lastYearLastDate ? `As Of ${lastYearLastDate}` : 'Last Year'}</span>,
      align: 'right',
      children: [
        {
          title: <span style={{ fontSize: '0.8rem' }}>Balance ( .Rs. )</span>,
          dataIndex: 'last_year_balance',
          key: 'last_year_balance',
          align: 'right',
          render: (text, record) => (
            <span
              style={{
                fontWeight:
                  record.type === TYPE_TOTAL || boldRows.includes(record.key) || record.is_heading === 1
                    ? 'bold'
                    : 'normal',
                fontSize: record.type === TYPE_TOTAL ? '0.83rem' : '0.8rem',
              }}
            >
              {record.is_heading === 1 ? '' : formatReportValue(text)}
            </span>
          ),
        },
      ],
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          title={
            <span style={{ fontSize: '22px', marginLeft: '35%' }}>
              {companyName + ' Stock Adjustment Report'}
            </span>
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={4} sm={2} md={1}></Col>
            <Col xs={15} sm={10} md={5}>
              <label>Date: *</label>
              <WidgetDatePicker
                value={date}
                setDate={(value) => changeValues('date', value)}
                placeholder="Date"
              />
            </Col>
            <Col xs={18} sm={12} md={6}>
              <label>Branch: *</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={8} sm={4} md={2}>
              <Button style={{ marginTop: 35 }} type="primary" htmlType="submit" disabled={isLoading}>
                Search
              </Button>
            </Col>
            <Col xs={8} sm={4} md={2}>
              <Button
                style={{ marginTop: 35 }}
                onClick={expandAllRows}
                disabled={isLoading || reportData.length === 0}
              >
                Expand All
              </Button>
            </Col>
            <Col xs={8} sm={4} md={2}>
              <Button
                style={{ marginTop: 35 }}
                onClick={collapseAllRows}
                disabled={isLoading || reportData.length === 0}
              >
                Collapse All
              </Button>
            </Col>
            <Col xs={6} sm={3} md={1}>
              <Button
                style={{ marginTop: 35, backgroundColor: '#28A745', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading || reportData.length === 0}
                icon={<FileExcelOutlined />}
              />
            </Col>
            <Col xs={6} sm={3} md={1}>
              <Button
                style={{ marginTop: 35, backgroundColor: '#FF0000', color: '#fff' }}
                onClick={exportToPdf}
                disabled={isLoading || reportData.length === 0}
                icon={<FilePdfOutlined />}
              />
            </Col>
          </Row>
        </Card>

        <br />
        <div style={{ overflow: 'auto', margin: 'auto', width: '80%' }}>
          <Table
            columns={columns}
            dataSource={reportData}
            pagination={false}
            expandable={{
              expandedRowKeys: expandedRows,
              expandIcon,
              onExpand: (expanded, record) => handleExpand(record, expanded),
            }}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
            loading={{ indicator: <div><Spin /></div>, spinning: isLoading }}
          />
        </div>
      </Form>
    </div>
  )
}

export default StockAdjustmentReport
